import React from 'react';
import { Star, MapPin, ExternalLink } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

interface Hotel {
  name: string;
  stars: number;
  image: string;
  address: string;
  amenities: string[];
  meals: string[];
  bookingUrl?: string;
}

interface Formula {
  id: number;
  name: string;
  priceType: 'PER_PERSON' | 'PER_ROOM';
  perPersonPrice: number | null;
  roomPrices: {
    single: number;
    double: number;
    triple: number;
    quadruple: number;
    quintuple: number;
  };
  discountPercent?: number | null;
  hotels: Hotel[];
}

interface PackageFormulasProps {
  formulas: Formula[];
  selectedFormulaId: number | undefined;
  onSelectFormula: (formula: Formula) => void;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
  };
}

function getDisplayPrice(formula: Formula) {
  if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
    return formula.perPersonPrice;
  }
  
  if (formula.roomPrices) {
    const prices = Object.values(formula.roomPrices).filter(price => price > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }

  return 0;
}

function formatPrice(formula: Formula) {
  if (formula.priceType === 'PER_PERSON') {
    const price = formula.perPersonPrice || 0;
    const formattedPrice = price.toLocaleString('fr-DZ');
    return `${formattedPrice} DA par personne`;
  } else {
    const prices = Object.values(formula.roomPrices).filter(price => price > 0);
    
    if (prices.length === 0) {
      return 'Prix sur demande';
    }

    const lowestPrice = Math.min(...prices);
    return `À partir de ${lowestPrice.toLocaleString('fr-DZ')} DA par personne`;
  }
}

function getRoomPricesDetails(formula: Formula) {
  if (formula.priceType !== 'PER_ROOM') return null;

  return Object.entries(formula.roomPrices)
    .filter(([_, price]) => price > 0)
    .map(([type, price]) => {
      const discountedPrice = formula.discountPercent 
        ? price * (1 - formula.discountPercent / 100)
        : price;

      return {
        type,
        label: {
          single: '1 personne',
          double: '2 personnes',
          triple: '3 personnes',
          quadruple: '4 personnes',
          quintuple: '5 personnes'
        }[type],
        capacity: {
          single: 1,
          double: 2,
          triple: 3,
          quadruple: 4,
          quintuple: 5
        }[type],
        price,
        discountedPrice
      };
    });
}

function HotelStars({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i}
          className={`w-4 h-4 ${i < count ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-1 text-sm text-gray-600">{count} étoiles</span>
    </div>
  );
}

function HotelCard({ hotel, theme }: { hotel: Hotel; theme: PackageFormulasProps['theme'] }) {
  const bookingUrl = hotel.bookingUrl?.trim();
  const isValidUrl = bookingUrl && (bookingUrl.startsWith('http://') || bookingUrl.startsWith('https://'));

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-white">
      <div className="flex gap-4">
        {hotel.image && (
          <img
            src={getImageUrl(hotel.image)}
            alt={hotel.name}
            className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-gray-800 truncate">{hotel.name}</p>
            <HotelStars count={Number(hotel.stars) || 0} />
          </div>
          {hotel.address && (
            <div className="flex items-center text-sm text-gray-500">
              <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="truncate">{hotel.address}</span>
            </div>
          )}
          {isValidUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-gray-50"
              style={{
                borderColor: theme.primaryColor,
                color: theme.primaryColor,
              }}
              title="Visiter le site de l'hôtel"
              aria-label="Visiter le site de l'hôtel"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Visiter le site de l'hôtel
            </a>
          )}
        </div>
      </div>

      {hotel.amenities.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1">
            {hotel.amenities.map((amenity, index) => (
              <span 
                key={index}
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ 
                  backgroundColor: `${theme.primaryColor}20`,
                  color: theme.primaryColor 
                }}
              >
                {amenity}
              </span>
            ))}
          </div>
        </div>
      )}

      {hotel.meals.length > 0 && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1">
            {hotel.meals.map((meal, index) => (
              <span 
                key={index}
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ 
                  backgroundColor: `${theme.secondaryColor}20`,
                  color: theme.secondaryColor 
                }}
              >
                {meal}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PackageFormulas({ 
  formulas, 
  selectedFormulaId, 
  onSelectFormula,
  theme 
}: PackageFormulasProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {formulas.map((formula) => {
        const maxStars = formula.hotels.length > 0
          ? Math.max(...formula.hotels.map(h => Number(h.stars) || 0))
          : 0;

        return (
          <div 
            key={formula.id}
            className={`border rounded-lg p-6 cursor-pointer transition-all ${
              selectedFormulaId === formula.id 
                ? 'border-2' 
                : 'border hover:border-gray-300'
            }`}
            style={{ 
              borderColor: selectedFormulaId === formula.id ? theme.primaryColor : '#e5e7eb'
            }}
            onClick={() => onSelectFormula(formula)}
          >
            {/* En-tête de la formule */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-lg font-semibold mb-1">{formula.name}</h4>
                {maxStars > 0 && <HotelStars count={maxStars} />}
              </div>
              <div className="text-right">
                <div className="text-lg font-bold" style={{ color: theme.primaryColor }}>
                  {formula.discountPercent && formula.discountPercent > 0 && (
                    <span 
                      className="line-through mr-2 text-sm"
                      style={{ color: '#181817' + '80' }}
                    >
                      {formatPrice(formula)}
                    </span>
                  )}
                  {formatPrice({
                    ...formula,
                    perPersonPrice: formula.perPersonPrice 
                      ? formula.perPersonPrice * (1 - (formula.discountPercent || 0) / 100)
                      : null,
                    roomPrices: Object.entries(formula.roomPrices).reduce((acc, [key, value]) => ({
                      ...acc,
                      [key]: value * (1 - (formula.discountPercent || 0) / 100)
                    }), {} as Formula['roomPrices'])
                  })}
                </div>
              </div>
            </div>

            {/* Hôtels */}
            <div className="space-y-4">
              {formula.hotels.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">
                    {formula.hotels.length === 1 ? 'Hôtel' : 'Hôtels'}
                  </h5>
                  <div className="space-y-2">
                    {formula.hotels.map((hotel, idx) => (
                      <HotelCard key={idx} hotel={hotel} theme={theme} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Prix des chambres */}
              {formula.priceType === 'PER_ROOM' && getRoomPricesDetails(formula)?.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">Prix par personne selon le type de chambre</h5>
                  <div className="flex flex-wrap gap-2">
                    {getRoomPricesDetails(formula)?.map(room => (
                      <div 
                        key={room.type}
                        className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5"
                        style={{ 
                          backgroundColor: `${theme.primaryColor}10`,
                          color: theme.primaryColor 
                        }}
                      >
                        <span className="font-medium">
                          Chambre {room.label}
                        </span>
                        {formula.discountPercent && formula.discountPercent > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="line-through text-gray-500">
                              {room.price.toLocaleString('fr-DZ')} DA
                            </span>
                            <span className="font-semibold">
                              {Math.round(room.discountedPrice).toLocaleString('fr-DZ')} DA / pers.
                            </span>
                          </div>
                        ) : (
                          <span className="font-semibold">
                            {room.price.toLocaleString('fr-DZ')} DA / pers.
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
